export const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

import { queryClient } from '@/lib/queryClient'

export const token = {
  // Auth is now via httpOnly cookie set by the server. clear() removes any
  // stale key left from sessions created before this migration.
  clear: () => localStorage.removeItem('ps_token'),
}


const USER_KEY = 'ps_user'

// Only auth-essential fields are stored in localStorage. Profile data (name, email,
// phone, businessName) is fetched via /api/v1/me and cached in React Query.
export interface StoredUser {
  id: number
  role_id: number
  role_name?: string
  must_change_password?: boolean
}

export const currentUser = {
  get: (): StoredUser | null => {
    try {
      const raw = localStorage.getItem(USER_KEY)
      return raw ? (JSON.parse(raw) as StoredUser) : null
    } catch {
      return null
    }
  },
  set: (u: { id: number; role_id: number; role_name?: string; must_change_password?: boolean }) =>
    localStorage.setItem(USER_KEY, JSON.stringify({ id: u.id, role_id: u.role_id, role_name: u.role_name, must_change_password: u.must_change_password })),
  clear: () => localStorage.removeItem(USER_KEY),
}


export class ApiError extends Error {
  readonly status: number
  readonly body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

function redirectToLogin() {
  token.clear()
  currentUser.clear()
  queryClient.clear()
  window.location.replace('/login')
}


async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: 'include',
      signal: init.signal ?? AbortSignal.timeout(90_000),
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError(0, 'Request timed out. Please try again.')
    }
    throw err
  }

  if (!res.ok) {
    let body: unknown
    try { body = await res.json() } catch { /* non-JSON error body */ }
    const message = (body as Record<string, string>)?.message ?? res.statusText
    if (res.status === 401 && path !== '/api/v1/auth/login') redirectToLogin()
    throw new ApiError(res.status, message, body)
  }

  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as T
  }
  return res.json() as Promise<T>
}


export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'GET' }),

  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'DELETE' }),

  downloadBlob: async (path: string, fallbackFilename: string): Promise<void> => {
    const headers = new Headers()
    const res = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' })
    if (!res.ok) {
      let body: unknown
      try { body = await res.json() } catch { /* non-JSON */ }
      if (res.status === 401) redirectToLogin()
      throw new ApiError(res.status, (body as Record<string, string>)?.message ?? res.statusText, body)
    }
    const blob = await res.blob()
    const disposition = res.headers.get('Content-Disposition')
    let filename = fallbackFilename
    if (disposition) {
      const m = disposition.match(/filename[^;=\n]*=(?:(["'])([^"'\n]*)\1|([^;\n]*))/)
      // Strip path separators and control chars to prevent path traversal / XSS
      if (m) filename = (m[2] || m[3] || fallbackFilename).trim().replace(/[/\\:*?"<>|\x00-\x1F]/g, '_')
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },

  logout: async () => {
    try {
      await request<void>('/api/v1/auth/logout', { method: 'POST' })
    } catch (err) {
      console.warn('[logout]', err)
    }
  },

  postForm: <T>(path: string, body: FormData) => {
    return fetch(`${BASE_URL}${path}`, { method: 'POST', credentials: 'include', body }).then(async (res) => {
      if (!res.ok) {
        let b: unknown
        try { b = await res.json() } catch { /* non-JSON */ }
        if (res.status === 401) redirectToLogin()
        throw new ApiError(res.status, (b as Record<string, string>)?.message ?? res.statusText, b)
      }
      return res.status === 204 ? undefined as T : res.json() as Promise<T>
    })
  },
}
